/**
 * Review marks: the draft as the player sees it (the server's list plus marks just made) and `mark`, which sends one mark per decision.
 * A mark shows at once (optimistic) and stays shown until the server's own list names it: a team state or draft copy at the same version
 * that predates the mark must not take it back. A refused or failed request takes it back and says why in `error`.
 * `unmark` is the sheet's quiet Undo: it sends `reviewed: false` and shows the decision unmarked at once, the same way.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import type { DraftView } from "@bsim/contracts";
import type { DecisionId } from "@bsim/domain";
import { useQueryClient } from "@tanstack/react-query";
import type { ApiClient } from "../api/client";
import { errorSentence } from "../errors";

export interface ReviewMarks {
  /** The draft with its marks as the player sees them: server list plus pending optimistic marks. Pass this to decisionReviewState/reviewTotals. */
  readonly draft: DraftView;
  /** Idempotent: marks `id` reviewed (optimistic, then draftReviewMark). No-op when already marked or not enabled. */
  readonly mark: (id: DecisionId) => void;
  /** Takes a Maintain back (optimistic, then draftReviewMark with `reviewed: false`); only a Maintained decision has a mark to clear. */
  readonly unmark: (id: DecisionId) => void;
  /** The sentence for the last refused mark, or null. */
  readonly error: string | null;
}

const NONE: readonly DecisionId[] = [];

/** The list a save answers with (an empty or unreadable answer carries none). */
const savedIds = (value: { readonly reviewedDecisionIds?: readonly DecisionId[] } | undefined): readonly DecisionId[] => value?.reviewedDecisionIds ?? NONE;

/** The saved list goes into the team state's draft (as `useDraft` does for its saves), so a remount reads the marks, not the copy from before them.
 * Merged with what the cache holds, so replies that arrive out of order cannot drop another mark: a mark unites, an unmark removes only its own id. */
function mergeMarks(old: unknown, returned: readonly DecisionId[], unmarked: DecisionId | null): unknown {
  if (typeof old !== "object" || old === null || !("draft" in old)) return old;
  const draft = (old as { draft: DraftView | null }).draft;
  if (draft === null) return old;
  const united = [...new Set([...(draft.reviewedDecisionIds ?? NONE), ...returned])];
  return { ...old, draft: { ...draft, reviewedDecisionIds: unmarked === null ? united : united.filter((x) => x !== unmarked) } };
}

export function useReviewMarks(api: ApiClient, teamId: string, roundId: string, draft: DraftView, enabled: boolean): ReviewMarks {
  const [pending, setPending] = useState<readonly DecisionId[]>([]);
  const [cleared, setCleared] = useState<readonly DecisionId[]>([]);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const inFlight = useRef(new Set<string>());
  const server = draft.reviewedDecisionIds ?? NONE;

  const mark = useCallback((id: DecisionId): void => {
    if (!enabled || inFlight.current.has(id) || (server.includes(id) && !cleared.includes(id))) return;
    inFlight.current.add(id);
    setCleared((c) => c.filter((x) => x !== id));
    setError(null);
    setPending((p) => (p.includes(id) ? p : [...p, id]));
    void api.call("draftReviewMark", { teamId, roundId }, { decisionId: id, reviewed: true }).catch(() => ({ ok: false as const, status: 0, error: { code: "INTERNAL" as const, message: "", requestId: "" } })).then((r) => {
      if (r.ok) { queryClient.setQueriesData({ queryKey: ["teamState", { teamId }] }, (old: unknown) => mergeMarks(old, savedIds(r.value), null)); return; }
      inFlight.current.delete(id);
      setPending((p) => p.filter((x) => x !== id));
      setError(errorSentence(r.error));
    });
  }, [api, queryClient, teamId, roundId, enabled, server, cleared]);

  const unmark = useCallback((id: DecisionId): void => {
    if (!enabled) return;
    setError(null);
    inFlight.current.delete(id);
    setPending((p) => p.filter((x) => x !== id));
    setCleared((c) => (c.includes(id) ? c : [...c, id]));
    void api.call("draftReviewMark", { teamId, roundId }, { decisionId: id, reviewed: false }).catch(() => ({ ok: false as const, status: 0, error: { code: "INTERNAL" as const, message: "", requestId: "" } })).then((r) => {
      if (r.ok) { queryClient.setQueriesData({ queryKey: ["teamState", { teamId }] }, (old: unknown) => mergeMarks(old, savedIds(r.value), id)); return; }
      setCleared((c) => c.filter((x) => x !== id));
      setError(errorSentence(r.error));
    });
  }, [api, queryClient, teamId, roundId, enabled]);

  const view = useMemo<DraftView>(() => {
    const gone = cleared.filter((id) => server.includes(id));
    const extra = pending.filter((id) => !server.includes(id));
    if (extra.length === 0 && gone.length === 0) return draft;
    return { ...draft, reviewedDecisionIds: [...server.filter((id) => !gone.includes(id)), ...extra] };
  }, [draft, pending, cleared, server]);
  return { draft: view, mark, unmark, error };
}
